import { create } from 'zustand';
import { ConversationRole } from '@evaltick/shared';
import type { InterviewQuestion } from '@/constants/questions';

export const AnswerMode = {
  Text: 'text',
  Voice: 'voice',
} as const;
export type AnswerMode = (typeof AnswerMode)[keyof typeof AnswerMode];

/**
 * What a voice turn was, judged from the phase that was active when it was
 * created. Answer is the load-bearing one: it's the filter that decides which
 * turns get assembled into the submitted answer, applied in four places
 * (useRealtimeVoice's kindForPhase/assembleAnswer, VoiceAnswerPanel's
 * MIN_CHARS gate, SessionInterview's mode-switch draft seeding), so it can't
 * be a bare literal any of them could typo into silently submitting nothing.
 */
export const ConversationEntryKind = {
  Answer: 'answer',
  Feedback: 'feedback',
  FollowUp: 'followUp',
} as const;
export type ConversationEntryKind =
  (typeof ConversationEntryKind)[keyof typeof ConversationEntryKind];

/**
 * One finalized or in-progress turn in a question's unified conversation —
 * voice or text, candidate or coach. Rendered by both VoiceAnswerPanel and
 * CoachPanel off the same per-question array, so either surface shows the
 * full conversation regardless of which mode produced which turn.
 */
export interface ConversationEntry {
  role: ConversationRole;
  content: string;
  /** True while still streaming/live — a text coach reply mid-stream, or a voice transcript not yet finalized. */
  isStreaming: boolean;
  /**
   * Voice-only tagging, stamped from the phase active when the turn was
   * created: Answer turns are what Stop assembles into the submitted answer;
   * Feedback/FollowUp are everything after. Absent on text-originated
   * entries, which have no such phase to tag from.
   */
  kind?: ConversationEntryKind;
  /** Client-generated id for a voice turn, reused as the Mastra message id on flush — absent for text entries, which Evaluate/Clarify already persist themselves. */
  clientTurnId?: string;
  /** Absolute epoch ms the turn was finalized — display only (e.g. FeedbackThread's timestamps); never used to order entries. Array order alone is the source of chronological truth. */
  occurredAtMs?: number;
}

interface InterviewState {
  assessmentId: string | null;
  /** The interview template's display name (e.g. "Healthcare Aide Interview") — shown in the session header. */
  templateName: string | null;
  /** From the purchased template's `coachingEnabled` — gates live feedback/follow-up chat in both modes. */
  coachingEnabled: boolean;
  questions: InterviewQuestion[];
  currentQuestionIndex: number;
  answers: string[];
  /**
   * What the candidate answered these same questions in an earlier sitting,
   * same order as `questions` — blank per question when there's no earlier
   * attempt. Set once at startSession and never mutated, unlike `answers`,
   * which starts as a copy of this and then tracks whatever they're editing
   * now. Consumers comparing "then vs now" must read this, not `answers`.
   */
  priorAnswers: string[];
  /**
   * Whether each question has a scored response on record, same order as
   * `questions` — a question is locked once its flag is set. A flag rather
   * than the response itself since resuming only ever gets AnsweredQuestion
   * payloads, which carry no scores.
   */
  answeredQuestions: boolean[];
  threadIds: string[];
  /** Per-question unified transcript — voice and text turns, candidate and coach, in true chronological order. */
  conversations: ConversationEntry[][];
  answerModes: AnswerMode[];
  isStreaming: boolean;
}

interface InterviewActions {
  startSession: (params: {
    assessmentId: string;
    templateName: string;
    coachingEnabled: boolean;
    questions: InterviewQuestion[];
    /** The candidate's earlier-sitting answers when retaking a subset of
     * questions, same order as `questions` — seeds both `priorAnswers` and the
     * editable `answers`. Omit for a fresh sitting; both default to blank. */
    priorAnswers?: string[];
    defaultAnswerMode?: AnswerMode;
    /**
     * Where to pick the sitting up: `index` is the question to land on,
     * `answeredQuestions` marks the ones already answered, and `conversation`
     * rehydrates just that landing question's transcript. A brand-new sitting
     * passes index 0 with nothing answered, which is the same as omitting it.
     */
    resumeFrom?: {
      index: number;
      answeredQuestions: boolean[];
      conversation?: ConversationEntry[];
    };
  }) => void;
  setAnswer: (index: number, answer: string) => void;
  setThreadId: (index: number, threadId: string) => void;
  setAnswerMode: (index: number, mode: AnswerMode) => void;
  /** Replaces a question's whole transcript at once — the live voice session mirrors its array back through this. */
  setConversation: (index: number, entries: ConversationEntry[]) => void;
  /**
   * Marks an evaluate/clarify request in flight without narrating it into the
   * conversation. Voice mode needs the flag on its own: the coach has already
   * spoken its feedback, so there's no entry to stream into, but the request
   * is still running and must keep gating re-submission (see runEvaluate).
   */
  setStreaming: (streaming: boolean) => void;
  /** Appends one already-finalized entry (a voice turn, or a candidate's typed follow-up). */
  appendEntry: (index: number, entry: ConversationEntry) => void;
  /** Starts a new streaming coach entry — the text follow-up reply, or a live voice coach turn. */
  beginStreamingEntry: (index: number, kind?: ConversationEntry['kind']) => void;
  appendStreamingChunk: (index: number, chunk: string) => void;
  finalizeStreamingEntry: (index: number) => void;
  /** Locks a question once its answer is scored and on record server-side. */
  markQuestionAnswered: (index: number) => void;
  advanceQuestion: () => void;
  reset: () => void;
}

type InterviewStore = InterviewState & InterviewActions;

const EMPTY_STATE: InterviewState = {
  assessmentId: null,
  templateName: null,
  coachingEnabled: true,
  questions: [],
  currentQuestionIndex: 0,
  answers: [],
  priorAnswers: [],
  answeredQuestions: [],
  threadIds: [],
  conversations: [],
  answerModes: [],
  isStreaming: false,
};

export const useInterviewStore = create<InterviewStore>((set) => ({
  ...EMPTY_STATE,

  startSession: ({
    assessmentId,
    templateName,
    coachingEnabled,
    questions,
    priorAnswers: prior,
    defaultAnswerMode,
    resumeFrom,
  }) => {
    const priorAnswers = questions.map((_, i) => prior?.[i] ?? '');
    set({
      assessmentId,
      templateName,
      coachingEnabled,
      questions,
      currentQuestionIndex: resumeFrom?.index ?? 0,
      answers: [...priorAnswers],
      priorAnswers,
      answeredQuestions: resumeFrom?.answeredQuestions ?? new Array(questions.length).fill(false),
      threadIds: new Array(questions.length).fill(''),
      conversations: questions.map((_, i) => (i === resumeFrom?.index ? resumeFrom.conversation ?? [] : [])),
      answerModes: questions.map(() => defaultAnswerMode ?? AnswerMode.Text),
      isStreaming: false,
    });
  },

  setAnswer: (index, answer) =>
    set((state) => {
      const answers = [...state.answers];
      answers[index] = answer;
      return { answers };
    }),

  setThreadId: (index, threadId) =>
    set((state) => {
      const threadIds = [...state.threadIds];
      threadIds[index] = threadId;
      return { threadIds };
    }),

  setAnswerMode: (index, mode) =>
    set((state) => {
      const answerModes = [...state.answerModes];
      answerModes[index] = mode;
      return { answerModes };
    }),

  setConversation: (index, entries) =>
    set((state) => {
      const conversations = state.conversations.map((c, i) => (i === index ? entries : c));
      return { conversations };
    }),

  appendEntry: (index, entry) =>
    set((state) => {
      const conversations = state.conversations.map((c, i) => (i === index ? [...c, entry] : c));
      return { conversations };
    }),

  setStreaming: (streaming) => set({ isStreaming: streaming }),

  beginStreamingEntry: (index, kind) =>
    set((state) => {
      const conversations = state.conversations.map((c, i) =>
        i === index
          ? [...c, { role: ConversationRole.Coach, content: '', isStreaming: true, kind }]
          : c
      );
      return { conversations, isStreaming: true };
    }),

  appendStreamingChunk: (index, chunk) =>
    set((state) => {
      const conversations = state.conversations.map((c, i) => {
        if (i !== index) return c;
        const updated = [...c];
        const last = updated[updated.length - 1];
        if (last?.isStreaming) {
          updated[updated.length - 1] = { ...last, content: last.content + chunk };
        }
        return updated;
      });
      return { conversations };
    }),

  finalizeStreamingEntry: (index) =>
    set((state) => {
      const conversations = state.conversations.map((c, i) => {
        if (i !== index) return c;
        return c.map((entry) =>
          entry.isStreaming
            ? { ...entry, isStreaming: false, occurredAtMs: entry.occurredAtMs ?? Date.now() }
            : entry
        );
      });
      return { conversations, isStreaming: false };
    }),

  markQuestionAnswered: (index) =>
    set((state) => {
      const answeredQuestions = [...state.answeredQuestions];
      answeredQuestions[index] = true;
      return { answeredQuestions };
    }),

  advanceQuestion: () =>
    set((state) => {
      const nextIndex = Math.min(state.currentQuestionIndex + 1, state.questions.length - 1);
      const answerModes = [...state.answerModes];
      answerModes[nextIndex] = state.answerModes[state.currentQuestionIndex] ?? AnswerMode.Text;
      return { currentQuestionIndex: nextIndex, answerModes };
    }),

  reset: () => set(EMPTY_STATE),
}));
